import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { projectsApi } from '@/api/mapApi';
import { ApiError } from '@/api/client';

interface Project {
  id: string;
  name: string;
  description: string | null;
  center_lat: number;
  center_lng: number;
  zoom_level: number;
  is_public: number;
  created_at: string;
}

export function ProjectsListPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const data = await projectsApi.list();
      setProjects(data.items ?? []);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-full flex flex-col">
      <Header />
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-4xl mx-auto p-4">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Đồ án quy hoạch</h1>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 text-sm font-medium"
            >
              + Tạo đồ án
            </button>
          </div>

          {loading && <p className="text-gray-500">Đang tải...</p>}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && projects.length === 0 && (
            <div className="bg-white rounded-lg p-8 text-center">
              <p className="text-gray-500 mb-4">Chưa có đồ án nào</p>
              <button
                onClick={() => setShowCreate(true)}
                className="text-brand-600 hover:underline"
              >
                Tạo đồ án đầu tiên
              </button>
            </div>
          )}

          <div className="space-y-3">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/admin/projects/${p.id}`}
                className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">{p.name}</h2>
                    {p.description && (
                      <p className="text-sm text-gray-500 line-clamp-1">{p.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Tạo: {new Date(p.created_at).toLocaleDateString('vi-VN')}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ml-2 ${
                      p.is_public ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {p.is_public ? 'Công khai' : 'Riêng tư'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            navigate(`/admin/projects/${id}`);
          }}
        />
      )}
    </div>
  );
}

interface CreateProjectModalProps {
  onClose: () => void;
  onCreated: (id: string) => void;
}

function CreateProjectModal({ onClose, onCreated }: CreateProjectModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const project = await projectsApi.create({
        name,
        description: description || undefined,
        // Default center: Hanoi
        centerLat: 21.0285,
        centerLng: 105.8542,
        zoomLevel: 15,
      });
      onCreated(project.id);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Tạo đồ án thất bại');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Tạo đồ án mới</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tên đồ án *</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="VD: Quy hoạch khu dân cư xóm 5"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mô tả</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
            />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={submitting || !name}
              className="px-4 py-2 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting ? 'Đang tạo...' : 'Tạo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
